import { type HTMLAttributes } from 'react';

type SkeletonProps = HTMLAttributes<HTMLDivElement>;

export function Skeleton({ className = '', ...props }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700 ${className}`}
      {...props}
    />
  );
}

export function SkeletonText({
  lines = 3,
  className = '',
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-4" style={{ width: i === lines - 1 ? '60%' : '100%' }} />
      ))}
    </div>
  );
}

export function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`rounded-lg border border-gray-200 p-4 dark:border-gray-700 ${className}`}>
      <Skeleton className="mb-4 h-6 w-1/3" />
      <SkeletonText lines={3} />
    </div>
  );
}

export function SkeletonSajuResult() {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Skeleton className="mx-auto mb-2 h-8 w-48" />
        <Skeleton className="mx-auto h-4 w-32" />
      </div>

      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-200 p-4 text-center dark:border-gray-700"
          >
            <Skeleton className="mx-auto mb-2 h-4 w-12" />
            <Skeleton className="mx-auto mb-1 h-8 w-8" />
            <Skeleton className="mx-auto h-8 w-8" />
          </div>
        ))}
      </div>

      <SkeletonCard />
    </div>
  );
}

export function SkeletonChatMessage({ isUser = false }: { isUser?: boolean }) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg p-3 ${
          isUser ? 'bg-[var(--color-primary)] text-white' : 'bg-gray-100 dark:bg-gray-800'
        }`}
      >
        <SkeletonText lines={2} />
      </div>
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8 flex items-center justify-center gap-2">
        <Skeleton className="h-8 w-24 rounded-full" />
        <div className="h-0.5 w-8 bg-gray-200 dark:bg-gray-700" />
        <Skeleton className="h-8 w-24 rounded-full" />
        <div className="h-0.5 w-8 bg-gray-200 dark:bg-gray-700" />
        <Skeleton className="h-8 w-24 rounded-full" />
      </div>

      <div className="mx-auto max-w-md space-y-6">
        <div className="mb-8 text-center">
          <Skeleton className="mx-auto mb-2 h-8 w-48" />
          <Skeleton className="mx-auto h-4 w-64" />
        </div>

        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

export function HomePageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-12">
      <section className="py-16 text-center">
        <Skeleton className="mx-auto mb-4 h-10 w-80" />
        <Skeleton className="mx-auto mb-2 h-5 w-96" />
        <Skeleton className="mx-auto mb-8 h-5 w-64" />
        <Skeleton className="mx-auto h-12 w-40 rounded-lg" />
      </section>

      <section className="py-16">
        <Skeleton className="mx-auto mb-12 h-8 w-32" />
        <div className="grid gap-8 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
